<template>
  <base-modal-ex :active.sync="isActive"
    :animation-in="animationIn"
    :animation-out="animationOut"
    :animation-duration="animationDuration"
    :can-cancel="false"
    :min-width="45"
    :min-height="55"
    has-modal-card>
    <header class="modal-card-head">
      <p class="modal-card-title"
        :class="{ 'is-flex-items-center' : showIcon}">
        <span v-if="showIcon"
          class="icon is-medium"
          :class="`has-text-${iconType}`"
          style="margin-right: 0.5rem">
          <i class="mdi mdi-36px"
            :class="`${iconName}`" />
        </span>
        <slot name="text-title" />
      </p>
    </header>
    <section class="modal-card-body">
      <advance-receipt-detail v-model="entity"
        :disable-change="entity.isNew"
        :show-amount="showAmount"
        :read-only-view="readOnlyView" />
    </section>
    <footer class="modal-card-foot"
      :style="`justify-content: flex-${buttonLocation}`">
      <button class="button is-success tooltip"
        v-show="entity.isNew"
        @click="save()"
        data-tooltip="Save">Save</button>
      <button class="button is-success tooltip"
        v-show="!entity.isNew"
        @click="print()"
        data-tooltip="Print">Print</button>
      <button class="button tooltip"
        @click="close()"
        data-tooltip="Cancel">Cancel</button>
    </footer>
  </base-modal-ex>
</template>

<script>
import { BaseModalEx } from '@/components/BulmaModal'
import { mapGetters } from 'vuex'
import AdvanceReceiptDetail from './AdvanceReceiptDetail'
import AdvanceReceiptValidation from './AdvanceReceiptValidation'
import AdvanceReceiptService from './AdvanceReceiptService'
import storeMixin from './storeMixin'
import StoreUtil from '@/store/utils'
//import Guid from '@/components/Guid'
import HttpStatus from '@/components/http-status'
import _debounce from 'lodash.debounce'
import _cloneDeep from 'lodash/cloneDeep'
import { Emailer } from '@/classes'
import { KeyValuePairModel } from '@/classes/viewmodels'
import PrintPreviewRoutes from '@/components/printpreview/route-types'
import { EventHubTypes } from '@/enums'
import _isEmpty from 'lodash/isEmpty'

export default {
  name: 'AdvanceReceiptModal',
  components: {
    BaseModalEx,
    AdvanceReceiptDetail
  },
  mixins: [AdvanceReceiptValidation, storeMixin],
  props: {
    active: {
      type: Boolean,
      default: false
    },
    animationIn: {
      type: String,
      default: 'zoomIn'
    },
    animationOut: {
      type: String,
      default: 'zoomOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    },
    buttonLocation: {
      type: String,
      default: 'end' // valid values 'start' & 'end'
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconType: {
      type: String,
      default: 'warning'
    },
    iconName: {
      type: String,
      default: 'mdi-alert-decagram'
    },
    showAmount: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isActive: false,
      entity: null
    }
  },
  computed: {
    ...mapGetters(['licenceExpired']),
    validateError() {
      return this.$v.$error || this.$v.detailGroup.$error
    }
  },
  watch: {
    active(value) {
      this.isActive = value
    },
    entity: {
      handler: _debounce(function(newVal) {
        if (newVal) {
          this.$forceUpdate()
          this.saveSnapshot(_cloneDeep(this.entity))
        }
      }, 500),
      deep: true
    }
  },
  beforeDestroy() {
    if (_isEmpty(this.returnRoute)) {
      this.setReadOnlyView(false)
    }
  },
  async created() {
    //this.entity = _cloneDeep(this.currentSnapshot)
    await this.getEntity()
  },
  mounted() {
    this.isActive = this.active || false
  },
  methods: {
    removeQueries() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|advancereceipt|returnUrl`)
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|advancereceipt|type`)
    },
    clearSessionStorage() {
      this.removeQueries()
    },
    async getEntity(reset = false) {
      try {
        this.$showSpinner()
        if (reset) {
          this.clearSessionStorage()
          this.clearSnapshots(this.$route.params.invoicePaymentId)
        }
        if (!this.currentSnapshot) {
          this.entity = await this.getStoreItem(this.entity.invoicePaymentId)
        }
        this.entity = _cloneDeep(this.currentSnapshot)
      } catch (e) {
        this.$notification.openMessageXhrError('', e)
      }
      this.$hideSpinner()
    },
    async save() {
      const title = 'Advance Receipt'
      if (this.validateError) {
        this.$notification.openNotificationWithType('danger', title, 'Validation errors. Please fix before saving')
        return false
      } else if (this.entity.amountPaid === 0) {
        this.$notification.openNotificationWithType('danger', title, 'Receipt total cannot be 0')
        return false
      }
      try {
        this.$showSpinner('Saving...')
        let response
        if (this.entity.isNew) {
          response = await AdvanceReceiptService.postEntity(this.entity)
        } else {
          this.$notification.openNotificationWithType('warning', title, 'No changes. Not saved')
        }
        this.$hideSpinner()
        if (this.isSaveContinue) {
          this.$notification.openNotificationWithType('success', title, isDelete ? 'Advance Receipt deleted' : 'Save successful')
        } else if (response && response.status === HttpStatus.NO_CONTENT) {
          await this.getEntity(true)
          this.$notification.openNotificationWithType('success', title, isDelete ? 'Advance Receipt deleted' : 'Save successful')
        } else if (response && response.status === HttpStatus.CREATED) {
          await this.getEntity(true)
          this.$notification.openNotificationWithType('success', title, 'New Advance Receipt added')
        }
        this.$eventHub.$emit(EventHubTypes.EntitySaved)
        return true
      } catch (e) {
        this.$hideSpinner()
        if (e.response.request.status === HttpStatus.CONFLICT) {
          this.isSaveConflictModalActive = true
        } else {
          this.$notification.openMessageXhrError('Advance Receipt', e)
        }
        return false
      }
    },
    close() {
      this.clearSessionStorage()
      //this.reset()
      this.$emit('update:active', false)
      this.$emit('close')
    },
    print() {
      const params = {
        COMPANYNAME: this.$userInfo.companyName,
        COMPANYID: this.$userInfo.companyId,
        INVOICEID: this.entity.invoiceId,
        REPORTTITLE: 'ADVANCE RECEIPT',
        timezoneoffset: this.$filters.getSiteTimezoneOffset()
      }
      // console.log('timezoneoffset',-new Date().getTimezoneOffset() / 60)

      const emailer = new Emailer()
      emailer.subject = 'ADVANCE RECEIPT'
      emailer.reportName = 'AT_Invoice'
      this.addEmailer(emailer)
      StoreUtil.setLocalStorage(emailer.id, 'emailer', emailer)

      const keyValuePairs = KeyValuePairModel.convertToKeyValuePairs(params)
      const parameterId = this.$guid.newGuid()
      StoreUtil.setLocalStorage(parameterId, 'parameters', keyValuePairs)

      this.addReportParameters(keyValuePairs)

      // this.$router.push({
      //   name: PrintPreviewRoutes.PrintPreview.name,
      //   params: { reportName: 'AT_Invoice' },
      //   query: { parameterId: this.$guid.newGuid(), emailerId: emailer.id }
      // })

      //open print report in a new tab
      const routeData = this.$router.resolve({
        name: PrintPreviewRoutes.PrintPreview.name,
        params: { reportName: 'AT_InvoiceV2' },
        query: { parameterId: parameterId, emailerId: emailer.id, isNewTab: true }
      })
      window.open(routeData.href, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.is-flex-items-center {
  display: flex;
  align-items: center;
}
</style>
